import {
  ActionIcon,
  Box,
  Button,
  Container,
  Flex,
  Modal,
  Rating,
  Switch,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { openConfirmModal } from "@mantine/modals";
import { IconCheck, IconEye, IconTrash, IconX } from "@tabler/icons";
import AppBreadcrumb from "components/AppBreadcrumb";
import produce from "immer";
import _ from "lodash";
import { MantineReactTable } from "mantine-react-table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { showErrorToast } from "utilities/Toast";
import moment from "moment";
import { useLocation } from "react-router-dom";

import { feedback_api } from "./feedback.service";

/**
 *
 * @param {{ review : Object }} props
 */

const delete_confirm_props = (deleteFunction, id) => {
  return {
    title: "Please confirm delete feedback",
    children: <Text size="sm">Are you sure you want to delete selected feedback ?</Text>,
    labels: { confirm: "Delete Feedback", cancel: "Cancel" },
    onCancel: () => console.log("Cancel"),
    onConfirm: async () => await deleteFunction(id),
    confirmProps: { color: "red" },
  };
};

export default function FeedbackList(props) {
  const pageTitle = "Feedback List";
  const tableInstanceRef = useRef(null);
  const theme = useMantineTheme();
  const location = useLocation();

  let isMobileScreen = useMediaQuery("(max-width: 768px)");

  const [feeaback, setFeedback] = useState([]);

  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [modalFeedback, setModalFeedback] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const queryMode = new URLSearchParams(location.search).get("mode");
  const [viewMode, setViewMode] = useState(queryMode || location.state?.viewMode || "feedback");

  const [previousLastSeenId] = useState(() => {
    const val = localStorage.getItem("lastSeenAreaFeedbackId");
    return val ? parseInt(val, 10) : 0;
  });

  useEffect(() => {
    const modeParam = new URLSearchParams(location.search).get("mode");
    if (modeParam) {
      setViewMode(modeParam);
    }
  }, [location.search]);

  const feeabacks_data = useQuery({
    queryKey: ["feedbacks-data", pagination.pageIndex, pagination.pageSize],
    queryFn: () => feedback_api.get(pagination.pageSize, pagination.pageIndex + 1),
    refetchInterval: 10000,
  });

  const area_feedbacks_query = useQuery({
    queryKey: ["area-feedbacks-data"],
    queryFn: () => feedback_api.getAreaFeedback(),
    refetchInterval: 10000,
  });

  useEffect(() => {
    if (viewMode === "area" && area_feedbacks_query.data?.data) {
      const dataList = area_feedbacks_query.data.data;
      if (dataList.length > 0) {
        const maxId = Math.max(...dataList.map(item => item.id));
        localStorage.setItem("lastSeenAreaFeedbackId", maxId.toString());
      } else {
        localStorage.setItem("lastSeenAreaFeedbackId", "0");
      }
      window.dispatchEvent(new Event("areaFeedbackSeen"));
    }
  }, [viewMode, area_feedbacks_query.data]);

  useEffect(() => {
    window.dispatchEvent(new Event("checkNotifications"));
  }, [pagination]);

  const handleToggleViewMode = () => {
    setViewMode((prev) => {
      const next = prev === "feedback" ? "area" : "feedback";
      return next;
    });
    // Trigger immediate refetch and notification check
    feeabacks_data.refetch();
    area_feedbacks_query.refetch();
    window.dispatchEvent(new Event("checkNotifications"));
  };


  const columns = useMemo(
    () => [
      {
        header: "User",
        accessorKey: "user",
        size: "auto",
        Cell: ({ cell }) => <Text tt={"capitalize"}>{cell.getValue()}</Text>,
      },
      {
        header: "Title",
        accessorKey: "title",
        size: "auto",
      },
      {
        header: "Description",
        accessorKey: "description",
        size: "auto",
        Cell: ({ renderedCellValue, cell, row }) => (
          <Box>
            <Text
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
              maw={400}
            >
              {row.original.description}
            </Text>
          </Box>
        ),
      },
    ],
    []
  );

  const areaColumns = useMemo(
    () => [
      {
        header: "User Name",
        accessorKey: "user_name",
        size: "auto",
        Cell: ({ cell }) => <Text tt={"capitalize"}>{cell.getValue()}</Text>,
      },
      {
        header: "Area Name",
        accessorKey: "area_name",
        size: "auto",
      },
      {
        header: "Mobile Number",
        accessorKey: "mobile_number",
        size: "auto",
      },
      {
        header: "Description",
        accessorKey: "description",
        size: "auto",
        Cell: ({ renderedCellValue, cell, row }) => (
          <Box>
            <Text
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
              maw={400}
            >
              {row.original.description}
            </Text>
          </Box>
        ),
      },
      {
        header: "Created At",
        accessorKey: "created_at",
        size: "auto",
        Cell: ({ cell }) => (
          <span>
            {cell.getValue() ? moment(cell.getValue()).format("DD-MM-YYYY HH:mm A") : "-"}
          </span>
        ),
      },
    ],
    []
  );

  const showHeader = !props.onlyTable && !isMobileScreen;

  return (
    <>
      {showHeader && (
        <Container fluid>
          <Box px={isMobileScreen ? 0 : 25} py={isMobileScreen ? 15 : 25}>
            <Flex justify={"space-between"} align="end" wrap={"wrap"}>
              <Box>
                <Text size={"lg"} fw={600} mb={10}>
                  {pageTitle}
                </Text>
                <AppBreadcrumb />
              </Box>
              <Box>
                <Button
                  color="brand"
                  onClick={handleToggleViewMode}
                >
                  {viewMode === "feedback" ? "Area Feedback" : "General Feedback"}
                </Button>
              </Box>
            </Flex>
          </Box>
        </Container>
      )}

      <Container fluid mt={10} mb={30}>
        <Box px={isMobileScreen ? 5 : props.onlyTable ? 0 : 25} sx={{ borderRadius: 2 }}>
          {!showHeader && (
            <Flex justify="flex-end" mb={15}>
              <Button
                color="brand"
                onClick={handleToggleViewMode}
              >
                {viewMode === "feedback" ? "Area Feedback" : "General Feedback"}
              </Button>
            </Flex>
          )}

          <Box className="datatable-wrapper">
            {viewMode === "feedback" ? (
              <MantineReactTable
                key="general-feedback-table"
                // tableInstanceRef={tableInstanceRef}
                mantineTableProps={{ className: "datatable" }}
                enableRowActions
                positionActionsColumn="last"
                renderTopToolbarCustomActions={({ row }) => (
                  <Box px={isMobileScreen ? 5 : 24} py={10}>
                    <Text
                      fw={600}
                      tt="uppercase"
                      size={"sm"}
                      sx={theme => ({
                        letterSpacing: 1,
                        color: theme.colors.brand[7],
                      })}
                    >
                      Feedback List
                    </Text>
                  </Box>
                )}
                renderRowActions={({ row }) => (
                  <Flex>
                    <Tooltip label="View Feedback">
                      <ActionIcon
                        sx={theme => ({
                          color: theme.colors.brand[7],
                        })}
                        onClick={() => {
                          setFeedbackModalVisible(true);
                          setModalFeedback(row.original);
                          window.dispatchEvent(new Event("checkNotifications"));
                        }}
                      >
                        <IconEye style={{ width: 20 }} />
                      </ActionIcon>
                    </Tooltip>
                  </Flex>
                )}
                initialState={{ density: "xs" }}
                state={{
                  isLoading: feeabacks_data.isLoading || feeabacks_data.isRefetching,
                  pagination,
                }}
                onPaginationChange={setPagination}
                manualPagination
                columns={columns}
                getRowId={originalRow => originalRow.id}
                enableDensityToggle={false}
                enableFullScreenToggle={isMobileScreen}
                enableColumnActions={false}
                rowCount={feeabacks_data.data?.data?.count}
                data={feeabacks_data.data?.data?.data || []}
                enableStickyHeader
                enableGlobalFilter={true} //turn off a feature
              />
            ) : (
              <MantineReactTable
                key="area-feedback-table"
                mantineTableProps={{ className: "datatable" }}
                mantineTableBodyRowProps={({ row }) => {
                  const isNew = row.original.id > previousLastSeenId;
                  return {
                    sx: (theme) => ({
                      backgroundColor: isNew
                        ? theme.colorScheme === "dark"
                          ? "rgba(47, 158, 68, 0.15)"
                          : "rgba(211, 249, 216, 0.6)"
                        : undefined,
                      "& td": isNew
                        ? {
                            backgroundColor: theme.colorScheme === "dark"
                              ? "rgba(47, 158, 68, 0.15)"
                              : "rgba(211, 249, 216, 0.6)",
                          }
                        : undefined,
                      "&:hover": {
                        backgroundColor: isNew
                          ? theme.colorScheme === "dark"
                            ? "rgba(47, 158, 68, 0.25)"
                            : "rgba(178, 242, 196, 0.7)"
                          : undefined,
                        "& td": isNew
                          ? {
                              backgroundColor: theme.colorScheme === "dark"
                                ? "rgba(47, 158, 68, 0.25)"
                                : "rgba(178, 242, 196, 0.7)",
                            }
                          : undefined,
                      },
                    }),
                  };
                }}
                enableRowActions
                positionActionsColumn="last"
                renderTopToolbarCustomActions={({ row }) => (
                  <Box px={isMobileScreen ? 5 : 24} py={10}>
                    <Text
                      fw={600}
                      tt="uppercase"
                      size={"sm"}
                      sx={theme => ({
                        letterSpacing: 1,
                        color: theme.colors.brand[7],
                      })}
                    >
                      Area Feedback List
                    </Text>
                  </Box>
                )}
                renderRowActions={({ row }) => (
                  <Flex>
                    <Tooltip label="View Feedback">
                      <ActionIcon
                        sx={theme => ({
                          color: theme.colors.brand[7],
                        })}
                        onClick={() => {
                          setFeedbackModalVisible(true);
                          setModalFeedback(row.original);
                          window.dispatchEvent(new Event("checkNotifications"));
                        }}
                      >
                        <IconEye style={{ width: 20 }} />
                      </ActionIcon>
                    </Tooltip>
                  </Flex>
                )}
                initialState={{ density: "xs" }}
                state={{
                  isLoading: area_feedbacks_query.isLoading || area_feedbacks_query.isRefetching,
                }}
                columns={areaColumns}
                getRowId={originalRow => originalRow.id}
                enableDensityToggle={false}
                enableFullScreenToggle={isMobileScreen}
                enableColumnActions={false}
                data={area_feedbacks_query.data?.data || []}
                enableStickyHeader
                enableGlobalFilter={true}
              />
            )}
          </Box>
        </Box>
      </Container>

      <Modal
        opened={feedbackModalVisible}
        onClose={() => {
          setFeedbackModalVisible(false);
          feeabacks_data.refetch();
          area_feedbacks_query.refetch();
          window.dispatchEvent(new Event("checkNotifications"));
        }}
        title={
          <Box px={10}>
            <Text fw={600} size="lg"> See Feedback </Text>
          </Box>
        }
        size={"550px"}
        centered
        radius="md"
      >
        <Box p={10}>
          <Box
            p={16}
            mb={15}
            sx={(theme) => ({
              backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
              borderRadius: theme.radius.md,
              border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]}`,
            })}
          >
            <Flex direction="column" gap="sm">
              <Flex justify="space-between" align="center">
                <Text fw={600} size="sm" color="dimmed">User</Text>
                <Text fw={500} size="sm" tt="capitalize">{modalFeedback.user || modalFeedback.user_name || "N/A"}</Text>
              </Flex>
              {modalFeedback.area_name && (
                <Flex justify="space-between" align="center">
                  <Text fw={600} size="sm" color="dimmed">Area</Text>
                  <Text fw={500} size="sm">{modalFeedback.area_name}</Text>
                </Flex>
              )}
              {modalFeedback.mobile_number && (
                <Flex justify="space-between" align="center">
                  <Text fw={600} size="sm" color="dimmed">Mobile</Text>
                  <Text fw={500} size="sm">{modalFeedback.mobile_number}</Text>
                </Flex>
              )}
            </Flex>
          </Box>

          {modalFeedback.title && (
            <Text size="md" fw={600} mb={8} color="brand.8">
              {modalFeedback.title}
            </Text>
          )}

          <Box
            p={12}
            sx={(theme) => ({
              backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.gray[0],
              borderRadius: theme.radius.sm,
              minHeight: 80,
            })}
          >
            <Text size="sm" style={{ whiteSpace: "pre-line", lineHeight: 1.5 }}>
              {modalFeedback.description}
            </Text>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
